/** @file jointMarks: the joints and bones as wire bones (Blender-style), updated in place each frame. */
import type { Scene } from '@babylonjs/core/scene.js';
import type { PoseBuffer } from '@zilo/pose3d-pose/contract';
import type { View } from '@zilo/pose3d-render-core/camera/viewOf';
import { boneMarks } from './boneMarks';

/**
 * What the marks read each frame: the drawn pose (entity-relative positions, parents, hidden flags), the node names
 * in pose order, the selected names and their mirror twins, the hovered node (pose index), whether isolate is on, the
 * view (display z scale, screen axes) and `px`, scene units per CSS pixel (marks keep their CSS size at every zoom).
 */
export type MarksInput = {
  pose: PoseBuffer; names: readonly string[]; selected: readonly string[]; twins: readonly string[]; hover: number | null;
  isolate: boolean; view: View; px: number;
  /** Per node, the drawn image's reach past the joint in units (`leafLengths`); absent = the stub length. */
  leafLen?: ArrayLike<number>;
  /** Per node `(x, y)`, the pivot fraction of the part drawn on it (`jointFracs`); absent = 0.5 (half-pixel pivots). */
  fracs?: ArrayLike<number>;
};

/** `update(input)` once per frame before `scene.render()` (null hides the marks); `shown()` counts drawn bones. */
export type JointMarks = { update(input: MarksInput | null): void; shown(): number };

/**
 * The bone marks (`boneMarks`: a wire octahedron and a wire head circle per shown node, a root ground ring; one
 * thin-instanced mesh) in the overlay's marks group at full resolution; buffers grow with the node count.
 */
export function jointMarks(scene: Scene): JointMarks {
  const bones = boneMarks(scene);
  return { update: (input) => { bones.update(input); }, shown: () => bones.shown() };
}
